/**
 * Pre-publish audit orchestration: live cheap badge, Fix actions, and export gate.
 */

import { getActiveEdit } from '../editing/text-editor.js';
import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { reportError } from '../core/report-error.js';
import { bridge } from '../editing/bridge.js';
import { setActivePage } from '../pages/page-manager.js';
import { ensureAllPagesHydrated } from '../sections/section-manager.js';
import { auditPageCoverage, runAudit } from '../shared/audit-engine.js';
import { runFullAudit } from '../shared/full-audit-pipeline.js';
import { deriveAuditScope } from '../shared/audit-readiness-scope.js';
import { showUndoToast } from './undo-toast.js';
import {
  analyzeAdoptable,
  adoptAssets,
  rewriteAdoptedUrls,
  ADOPT_ASSETS_LABEL
} from '../core/asset-adoption.js';
import { assembleLiveTemplateCSS } from '../shared/template-support-css.js';
import { preparePortableState } from '../core/save-load.js';
import { versionContentHashOf } from '../shared/version-hash.js';
import { normalizeExportLogoSlots } from '../export/snapshot-transforms.js';
import { dedupeEditableIdsAcrossState } from '../editing/editable-id-repair-planner.js';
import { identityRepairNamespaceOf } from '../shared/editable-id-repair.js';
import { listProjectComments, listProjectMembers } from '../templates/auth-api.js';
import { collabIdentityRepairReadiness } from '../collab/collab-session.js';
import { isSectionLockedByPeer } from '../editing/collab-guard.js';
import { readActiveEditableIdentity } from '../editing/active-editable-store.js';
import { clearElementSelection, getElementSelectionContext } from '../editing/element-selection.js';
import { createEditableIdRepairAction } from './editable-id-repair-action.js';

const CHEAP_AUDIT_DEBOUNCE_MS = 300;
/** How long the project must be quiet before the full tier runs on idle (P7). */
const IDLE_FULL_AUDIT_MS = 2000;

// P7 idle scheduling. `requestIdleCallback` alone does NOT make synchronous
// work incremental — it only picks a nicer moment to block. What keeps the main
// thread responsive is the generation token below: an idle run that is
// superseded by a newer edit is DISCARDED rather than emitted, so a stale
// report can never overwrite a fresher one.
let idleHandle = 0;
let idleTimer = 0;
let auditGeneration = 0;
let lastFullAuditState = null;
let idleAbortController = null;
let activeAuditRun = null;
let auditDependencies = {};
let auditProjectGeneration = null;

const scheduleIdle =
  typeof requestIdleCallback === 'function'
    ? (fn) => requestIdleCallback(fn, { timeout: 3000 })
    : (fn) => setTimeout(fn, 0);
const cancelIdle =
  typeof cancelIdleCallback === 'function' ? (h) => cancelIdleCallback(h) : (h) => clearTimeout(h);

/**
 * Queue a full-tier run for when the project goes quiet. Every call supersedes
 * the previous one, so a burst of edits produces exactly one run.
 */
function cancelScheduledAudit() {
  if (idleTimer) clearTimeout(idleTimer);
  if (idleHandle) cancelIdle(idleHandle);
  idleTimer = 0;
  idleHandle = 0;
}

function cancelActiveAudit() {
  if (!activeAuditRun) return;
  const run = activeAuditRun;
  activeAuditRun = null;
  run.controller.abort();
  eventBus.emit('pre-publish-audit:run-settled', {
    runId: run.generation,
    projectId: run.projectId,
    ok: false
  });
}

function scheduleIdleFullAudit() {
  cancelScheduledAudit();
  cancelActiveAudit();
  const generation = ++auditGeneration;
  idleTimer = setTimeout(() => {
    idleTimer = 0;
    idleHandle = scheduleIdle(() => {
      idleHandle = 0;
      if (generation !== auditGeneration || (typeof document !== 'undefined' && document.hidden))
        return;
      runFullAuditForTrigger('idle', generation);
    });
  }, IDLE_FULL_AUDIT_MS);
}

async function runFullAuditForTrigger(trigger, scheduledGeneration = null) {
  if (scheduledGeneration === null) {
    cancelScheduledAudit();
    cancelActiveAudit();
    auditGeneration++;
  }
  const generation = scheduledGeneration ?? auditGeneration;
  const controller = new AbortController();
  idleAbortController = controller;
  const projectId = store.getState()?.projectId;
  const projectGeneration = store.getEpoch() - store.getRevision();
  const run = {
    generation,
    projectId,
    projectGeneration,
    epoch: store.getEpoch(),
    controller
  };
  activeAuditRun = run;
  const current = () =>
    activeAuditRun === run &&
    generation === auditGeneration &&
    !controller.signal.aborted &&
    store.getState()?.projectId === projectId &&
    store.getEpoch() - store.getRevision() === projectGeneration;
  eventBus.emit('pre-publish-audit:run-started', {
    runId: generation,
    projectId,
    trigger
  });
  let acceptedSnapshot = null;
  let ok = false;
  let progress = null;
  try {
    const snapshot = structuredClone(store.getState());
    const report = await runFullAudit(snapshot, {
      signal: controller.signal,
      scope: deriveAuditScope(lastFullAuditState, snapshot),
      shouldContinue: current,
      deps: auditDependencies,
      hydrate: async () => {
        const outcome = await (auditDependencies.ensureAllPagesHydrated || ensureAllPagesHydrated)({
          signal: controller.signal,
          allowPartial: true,
          onProgress: ({ done, total }) => {
            if (!current() || !showProgressToastFn) return;
            const message = `Loading pages for readiness checks… ${done} of ${total}`;
            if (!progress)
              progress = showProgressToastFn(message, {
                id: 'audit-readiness-hydrate'
              });
            else progress.update(message);
          }
        });
        if (!current()) return null;
        acceptedSnapshot = structuredClone(store.getState());
        return {
          snapshot: acceptedSnapshot,
          coverage: outcome?.coverage || auditPageCoverage(acceptedSnapshot),
          revision: store.getRevision()
        };
      }
    });
    if (!report || !current()) return null;
    if (!report.coverage.hydrationFailed.length) lastFullAuditState = acceptedSnapshot;
    emitReport({ ...report, trigger, runId: generation });
    ok = true;
    if (trigger === 'manual' && activePanelTab !== 'audit') maybeToastSummary(report);
    return report;
  } catch (err) {
    if (current() && err?.name !== 'AbortError')
      reportError('Readiness audit', err, {
        user: trigger === 'manual' ? 'toast' : 'silent'
      });
    return null;
  } finally {
    progress?.dismiss();
    // A cancelled run's late finally must never clear its successor's busy UI.
    if (activeAuditRun === run && generation === auditGeneration) {
      activeAuditRun = null;
      idleAbortController = null;
      eventBus.emit('pre-publish-audit:run-settled', {
        runId: generation,
        projectId,
        ok
      });
    }
  }
}

/** Test seam: cancel all pending work, including the cheap debounce. */
export function _cancelIdleAudit() {
  cancelScheduledAudit();
  cancelActiveAudit();
  if (cheapTimer) clearTimeout(cheapTimer);
  cheapTimer = 0;
  idleAbortController?.abort();
  idleAbortController = null;
  auditGeneration++;
}

// Second click cannot start a second adoption (repo rule: every async user
// action gets an in-flight guard).
let _adoptionInFlight = false;
let _exportInFlight = false;

let initialized = false;
let cheapTimer = 0;
let lastReport = null;
let activePanelTab = '';
let showToastFn = null;
let showProgressToastFn = null;
let repairEditableIds = null;
const repairRefusals = new Map();

function invalidateRepairedIdentities(instanceIds) {
  bridge.invalidateEditableIdentity(instanceIds);
  if (instanceIds.includes(getElementSelectionContext()?.instanceId))
    clearElementSelection('editable-id-repair');
  if (instanceIds.includes(readActiveEditableIdentity().instanceId)) {
    bridge.send('clear-selected-editable', { reason: 'editable-id-repair' });
    eventBus.emit('iframe:selected-editable-cleared', { reason: 'editable-id-repair' });
  }
  eventBus.emit('editable-ids:repaired', { instanceIds });
}

export function initPrePublishAuditActions({
  showToast,
  showProgressToast,
  auditDeps = {},
  getCurrentUser = () => null
} = {}) {
  if (initialized) return;
  initialized = true;
  showToastFn = showToast || null;
  showProgressToastFn = showProgressToast || null;
  auditDependencies = auditDeps;
  auditProjectGeneration = store.getEpoch() - store.getRevision();
  let repairProgress = null;
  repairEditableIds = createEditableIdRepairAction({
    store,
    getUser: getCurrentUser,
    getActiveTextEdit: getActiveEdit,
    namespaceOf: identityRepairNamespaceOf,
    hydrate: ensureAllPagesHydrated,
    listComments: listProjectComments,
    listMembers: listProjectMembers,
    readiness: collabIdentityRepairReadiness,
    isLocked: isSectionLockedByPeer,
    templateCSS: assembleLiveTemplateCSS,
    plan: dedupeEditableIdsAcrossState,
    invalidate: invalidateRepairedIdentities,
    captureRefusals: (refusals, snapshot) => {
      repairRefusals.clear();
      for (const refusal of refusals) {
        const section = refusal.sectionPath
          .split('.')
          .reduce((value, key) => value?.[key], snapshot);
        repairRefusals.set(refusal.instanceId, {
          projectId: snapshot.projectId,
          input: JSON.stringify(section),
          ...refusal
        });
      }
      runAndEmit('cheap');
    },
    pending: (active) => {
      if (active)
        repairProgress = showProgressToastFn?.('Checking element identities…', {
          id: 'editable-id-repair'
        });
      else {
        repairProgress?.dismiss();
        repairProgress = null;
      }
    },
    notify: (message, options) => {
      if (options.undo) showUndoToast(message, { id: 'undo-editable-id-repair' });
      else showToastFn?.(message, { type: options.type });
      if (options.refusals?.length)
        eventBus.emit('editable-ids:repair-refused', { refusals: options.refusals });
    }
  });
  const invalidateHistoryRepair = ({ patches, source } = {}) => {
    if (source && !['undo', 'redo'].includes(source)) return;
    const ids = (patches || [])
      .filter((patch) => patch.path?.endsWith('.editableIdRepairs'))
      .map((patch) => {
        const path = patch.path.slice(0, -'.editableIdRepairs'.length);
        return path.split('.').reduce((value, key) => value?.[key], store.getState())?.instanceId;
      })
      .filter(Boolean);
    if (ids.length) invalidateRepairedIdentities(ids);
  };
  eventBus.on('history-changed', invalidateHistoryRepair);
  eventBus.on('collab:remote-applied', invalidateHistoryRepair);

  eventBus.on('pre-publish-audit:run', (payload = {}) => {
    if (payload.tier === 'full') runFullAuditForTrigger('manual');
    else runAndEmit('cheap');
  });
  // Fix and Fix-all are user clicks. A console warning is not an outcome the
  // user can see, so a failed fix used to look exactly like a fix that did
  // nothing — the panel simply kept showing the finding.
  eventBus.on('pre-publish-audit:fix', (finding) => {
    dispatchFix(finding).catch((err) =>
      reportError('Audit fix', err, {
        user: 'toast',
        message: 'Could not jump to that issue. Try again.'
      })
    );
  });
  // v1.2 — cluster-level Fix-all. Cluster header (in ui/finding-list.js)
  // emits this with the first finding's clusterFix descriptor.
  eventBus.on('pre-publish-audit:cluster-fix', (clusterFix) => {
    dispatchClusterFix(clusterFix).catch((err) =>
      reportError('Audit fix all', err, {
        user: 'toast',
        // Deliberately does not claim "nothing changed" — the mutations run in
        // a store transaction, and a mid-batch throw leaves that state unproven.
        message: 'Could not fix every item in that group. Check the panel before exporting.'
      })
    );
  });
  eventBus.on('state-changed', () => {
    const projectGeneration = store.getEpoch() - store.getRevision();
    const projectChanged = projectGeneration !== auditProjectGeneration;
    auditProjectGeneration = projectGeneration;
    if (projectChanged) {
      lastFullAuditState = null;
      lastReport = null;
    }
    if (cheapTimer) clearTimeout(cheapTimer);
    cheapTimer = setTimeout(() => {
      cheapTimer = 0;
      runAndEmit('cheap');
    }, CHEAP_AUDIT_DEBOUNCE_MS);
    // Hydration's updateSilent calls notify without changing the epoch. They
    // may repaint cheap checks, but must not cancel the run loading the pages.
    const cacheFill =
      activeAuditRun && !projectChanged && activeAuditRun.epoch === store.getEpoch();
    if (!cacheFill) scheduleIdleFullAudit();
  });
  eventBus.on('panel:tab-changed', ({ tab } = {}) => {
    activePanelTab = tab || '';
  });
  eventBus.on('toolbar:operation-state', ({ type, busy } = {}) => {
    if (type === 'export') _exportInFlight = Boolean(busy);
  });
  eventBus.on('pre-publish-audit:navigate', (finding) => {
    navigateToFinding(finding).catch((err) =>
      reportError('Audit navigation', err, { user: 'silent' })
    );
  });

  runAndEmit('cheap');
}

function runAndEmit(tier, stateOverride = null) {
  const report = runAudit(stateOverride || store.getState(), {
    tier,
    revision: store.getRevision()
  });
  emitReport(report);
  return report;
}

function emitReport(report) {
  if (repairRefusals.size) {
    const state = store.getState();
    const findings = report.findings.map((finding) => {
      const refusal = repairRefusals.get(finding.location?.instanceId);
      if (
        !refusal ||
        finding.checkId !== 'eid-uniqueness' ||
        refusal.projectId !== state?.projectId
      )
        return finding;
      const section = refusal.sectionPath.split('.').reduce((value, key) => value?.[key], state);
      if (JSON.stringify(section) !== refusal.input) return finding;
      return {
        ...finding,
        title: "Needs manual review: duplicate IDs whose original can't be matched",
        detail: refusal.reason
      };
    });
    const byCheck = { ...report.byCheck };
    byCheck['eid-uniqueness'] = findings.filter((finding) => finding.checkId === 'eid-uniqueness');
    report = { ...report, findings, byCheck };
  }
  lastReport = report;
  eventBus.emit('pre-publish-audit:report', report);
}

function maybeToastSummary(report) {
  const actionable =
    (report?.countsBySeverity?.blocking || 0) + (report?.countsBySeverity?.warning || 0);
  if (!actionable || !showToastFn) return;
  showToastFn(`${actionable} publish audit item${actionable === 1 ? '' : 's'} found`, {
    type: 'info',
    duration: 4500,
    placement: 'bottom-right',
    action: {
      label: 'Review',
      onClick: () => openAuditTab()
    }
  });
}

export function getLastPrePublishAuditReport() {
  return lastReport;
}

function applyAuditPageSelection(state, pageIds) {
  if (!Array.isArray(pageIds) || pageIds.length === 0) return;
  const wanted = new Set(pageIds.map((id) => String(id)));
  const pages = (Array.isArray(state.pages) ? state.pages : []).filter((page) =>
    wanted.has(String(page?.id))
  );
  if (pages.length === 0) return;
  state.pages = pages;
  if (!state.pages.some((page) => page.id === state.activePage)) {
    state.activePage = state.pages[0].id;
  }
}

function buildExportAuditState({ features = null, pageIds = null } = {}) {
  const auditState = structuredClone(store.getState());
  if (features) auditState.features = features;
  applyAuditPageSelection(auditState, pageIds);
  return auditState;
}

/**
 * v1.2 — handle cluster-level Fix-all actions. Each kind owns its mutation
 * pattern. All mutations go through store.update inside a single transaction
 * so Cmd-Z reverts the whole batch as one entry. The cheap-tier audit re-fires
 * via the state-changed debounce, so the cluster disappears from the panel
 * within ~300ms once findings clear.
 */
async function dispatchClusterFix(clusterFix) {
  if (!clusterFix || typeof clusterFix !== 'object' || !clusterFix.kind) return;
  switch (clusterFix.kind) {
    case 'restamp-all-eids': {
      await repairEditableIds?.();
      return;
    }
    case 'adopt-assets': {
      await runAssetAdoption();
      return;
    }
    case 'mark-decorative': {
      markImagesDecorative(clusterFix.rows || []);
      return;
    }
    default:
      console.warn('[Audit] unknown cluster fix kind:', clusterFix.kind);
  }
}

/**
 * P2 asset adoption, as a cluster action. Downloads every state-owned remote
 * image into the project's AssetStore and rewrites the references.
 *
 * Ordering is the safety property: every download completes BEFORE state is
 * touched, so a failure never leaves a half-rewritten project. What did not
 * download keeps its original URL and is reported.
 */
async function runAssetAdoption() {
  if (_adoptionInFlight) return;
  if (_exportInFlight) {
    showToastFn?.('Wait for the current export before bringing images into the project', {
      type: 'info'
    });
    return;
  }
  _adoptionInFlight = true;
  let progress = null;
  try {
    const state = store.getState();
    let templateCSS = '';
    try {
      templateCSS = await assembleLiveTemplateCSS(state);
    } catch (err) {
      // Expected-flow fallback: without template CSS we simply cannot classify
      // template-owned URLs, so they are reported as "unknown" rather than
      // silently folded into the adoptable set.
      reportError('Asset adoption', err, {
        user: 'silent',
        detail: 'template CSS unavailable'
      });
    }
    const { stateOwned, templateOwned } = analyzeAdoptable(state, {
      templateCSS
    });

    if (stateOwned.length === 0) {
      showToastFn?.(
        templateOwned.length
          ? `Nothing to bring in — ${templateOwned.length} image${templateOwned.length === 1 ? '' : 's'} live in the template stylesheet`
          : 'No remote images to bring in',
        { type: 'info' }
      );
      return;
    }

    progress = showProgressToastFn?.(`Bringing in images… 0 of ${stateOwned.length}`, {
      id: 'adopt-assets'
    });
    const { map, failures } = await adoptAssets(stateOwned, {
      onProgress: ({ done, total }) => progress?.update(`Bringing in images… ${done} of ${total}`)
    });
    progress?.dismiss();
    progress = null;

    const updates = rewriteAdoptedUrls(store.getState(), map);
    if (updates.length === 0) {
      showToastFn?.('Could not bring in any images', { type: 'error' });
      return;
    }
    store.beginTransaction(ADOPT_ASSETS_LABEL);
    for (const u of updates) store.update(u.path, u.value, ADOPT_ASSETS_LABEL);
    store.commitTransaction();

    // The undo IS the confirmation (picker-patterns rule) — never a success
    // toast beside it.
    const n = map.size;
    const parts = [`Brought in ${n} image${n === 1 ? '' : 's'}`];
    if (failures.length) parts.push(`${failures.length} failed`);
    if (templateOwned.length) parts.push(`${templateOwned.length} in template CSS`);
    showUndoToast(parts.join(' · '), { id: 'undo-adopt-assets' });
  } finally {
    progress?.dismiss();
    _adoptionInFlight = false;
  }
}

export function markImagesDecorative(rows) {
  const state = store.getState();
  const byPath = new Map();
  for (const finding of rows || []) {
    const path = finding?.location?.sectionPath || finding?.fix?.payload?.sectionPath;
    const index = finding?.fix?.payload?.imageIndex;
    if (!path || !Number.isInteger(index)) continue;
    let html = byPath.get(path);
    if (html == null) {
      const parts = path.split('.');
      let value = state;
      for (const part of parts) value = value?.[part];
      html = typeof value === 'string' ? value : '';
    }
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    const image = root?.querySelectorAll('img')?.[index] || null;
    if (!image) continue;
    const expectedSrc = finding?.fix?.payload?.src || '';
    if (expectedSrc && image.getAttribute('src') !== expectedSrc) continue;
    image.setAttribute('alt', '');
    byPath.set(path, root.innerHTML);
  }
  if (!byPath.size) return false;
  store.beginTransaction('Mark images decorative');
  for (const [path, html] of byPath) store.update(path, html, 'Mark images decorative');
  store.commitTransaction();
  showUndoToast(`Marked ${rows.length} image${rows.length === 1 ? '' : 's'} decorative`, {
    id: 'undo-mark-images-decorative'
  });
  return true;
}

export async function dispatchFix(finding) {
  const fix = finding?.fix;
  if (!fix) return;
  switch (fix.kind) {
    case 'focus-client-field':
      openClientSeoTab();
      setTimeout(() => eventBus.emit('client-seo:focus-field', fix.payload || {}), 0);
      return;
    case 'focus-seo-field':
      await focusSeoField(finding);
      return;
    case 'navigate-to-hit':
      await navigateToFinding(finding);
      return;
    case 'open-link-editor':
      await navigateToFinding(finding);
      eventBus.emit('editing:request-link-popover', { finding });
      return;
    case 'open-image-replacer':
      await navigateToFinding(finding);
      eventBus.emit('editing:request-image-replace', { finding });
      return;
    case 'open-resync-prompt':
      openClientSeoTab();
      setTimeout(() => eventBus.emit('client-seo:focus-resync', {}), 0);
      return;
    default:
      return;
  }
}

async function focusSeoField(finding) {
  const payload = finding?.fix?.payload || {};
  const pageId = payload.pageId || finding?.location?.pageId || null;
  const fieldKey = payload.fieldKey || 'title';
  if (pageId) await switchPageIfNeeded(pageId);
  openClientSeoTab();
  setTimeout(() => eventBus.emit('client-seo:focus-seo-field', { pageId, fieldKey }), 0);
}

function openClientSeoTab() {
  eventBus.emit('sidebar:ensure-open');
  eventBus.emit('props:request-tab', { tab: 'client-seo' });
}

export function openAuditTab() {
  eventBus.emit('sidebar:ensure-open');
  eventBus.emit('props:request-tab', { tab: 'audit' });
}

async function navigateToFinding(finding) {
  const loc = finding?.location || {};
  const payload = finding?.fix?.payload || {};
  const pageId = loc.pageId || payload.pageId || null;
  if (pageId) await switchPageIfNeeded(pageId);

  const instanceId = payload.instanceId || loc.instanceId || null;
  const eid = payload.eid || loc.eid || null;
  if (!instanceId && !eid) return;

  bridge.send('scroll-to-eid', { instanceId, eid });
  if (loc.match) {
    bridge.send('highlight-text-match', {
      instanceId,
      eid,
      hitId: finding.id,
      beforeCtx: loc.beforeCtx || '',
      match: loc.match,
      afterCtx: loc.afterCtx || ''
    });
  }
}

async function switchPageIfNeeded(pageId) {
  const state = store.getState();
  if (!pageId || state?.activePage === pageId) return;
  const run = async () => setActivePage(pageId);
  if (typeof document !== 'undefined' && typeof document.startViewTransition === 'function') {
    try {
      await document.startViewTransition(run).finished;
      return;
    } catch {
      return;
    }
  }
  await run();
}

// `kind` is still accepted by callers; since gate v2 nothing here prompts, so
// the gate no longer needs to know which export it is fronting.
export async function readProjectReadiness({
  pageIds = null,
  features = null,
  signal = null
} = {}) {
  let projectId;
  let projectGeneration;
  const currentProject = () =>
    store.getState()?.projectId === projectId &&
    store.getEpoch() - store.getRevision() === projectGeneration;
  // Hydration can pull down every page of a large multi-page template before the
  // audit can even start. The only signal used to be the Export button's label,
  // which says "Preparing…" whether that takes 20 ms or 20 s. The toast is created
  // lazily from the first progress callback, so a fully-hydrated project — the
  // common case — still shows nothing at all.
  let progress = null;
  try {
    projectId = store.getState()?.projectId;
    projectGeneration = store.getEpoch() - store.getRevision();
    const hydration = await ensureAllPagesHydrated({
      signal,
      onProgress: ({ done, total }) => {
        if (!showProgressToastFn) return;
        const message = `Loading pages for the export audit… ${done} of ${total}`;
        if (!progress) progress = showProgressToastFn(message, { id: 'audit-gate-hydrate' });
        else progress.update(message);
      }
    });
    if (hydration?.coverage?.hydrationFailed.length)
      throw new Error(`Could not load pages: ${hydration.coverage.hydrationFailed.join(', ')}`);
    progress?.dismiss();
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    progress?.error('Could not load every page for the export audit');
    reportError('Export audit', err, {
      user: progress ? 'silent' : 'toast',
      message: 'Could not load every page for the export audit'
    });
    return null;
  }
  // Gate v2 (2026-08-24): this function NO LONGER refuses an export, at any
  // severity. It hydrates, runs the audit, and emits it so the audit tab and the
  // badge are current — then gets out of the way.
  //
  // Nothing is prompted here any more, blocking included. Two reasons, and they
  // are the same reason the warning prompt left in Part 0:
  //  1. The picture here is INCOMPLETE. It is state-only, pre-resolution, so it
  //     is missing every download-dependent finding (asset-download-failed,
  //     missing-asset) and it over-reports the ones the export is about to fix —
  //     `template-hotlink-assets` fires on assets the exporter downloads and
  //     bundles seconds later. Asking against that picture means a granted
  //     export can still be refused after the long wait, which reads as the
  //     answer being ignored.
  //  2. Since P8 there is no gate prompt at all. Recoverable findings ship
  //     stamped `autoOverridden` and are disclosed in export-report.html;
  //     anything off AUTO_SHIPPABLE_CHECKS still throws.
  try {
    if (!currentProject()) return null;
    const revision = store.getRevision();
    const snapshot = buildExportAuditState({ features, pageIds });
    normalizeExportLogoSlots(snapshot);
    const contentHash = await versionContentHashOf(preparePortableState(snapshot));
    const report = await runFullAudit(snapshot, {
      signal,
      shouldContinue: currentProject,
      deps: auditDependencies,
      hydrate: async () => ({
        snapshot,
        coverage: auditPageCoverage(snapshot),
        revision
      })
    });
    if (!report) return null;
    report.snapshotHash = contentHash;
    emitReport(report);
    return { snapshot, contentHash, report };
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    reportError('Export audit', err, {
      user: 'toast',
      message: 'Could not complete the export audit'
    });
    return null;
  }
}

/** Temporary compatibility export for callers outside the builder shell. */
export async function runPrePublishAuditGate(options = {}) {
  return !!(await readProjectReadiness(options));
}
